import { Search } from '@mui/icons-material'
import { Button, Checkbox, Chip, Stack, Typography } from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams
} from '@mui/x-data-grid'
import React from 'react'
import { connect } from 'react-redux'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { IncidentReport } from 'types/incident-report'
import {
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  incidentReport: IncidentReportPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    incidentReport: IncidentReportOperations
  }
}

interface OwnProps {
  incidentReports: IncidentReport[]
  loading: boolean
  onView: (id: number) => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const IncidentReportTableFC: React.FC<any> = (props: AppProps) => {
  const { incidentReports, operations, loading, onView } = props

  const toggleTrack = (id: number, tracked: boolean): void => {
    void post(
      `/report/${id}/`,
      { tracked: !tracked },
      false,
      false,
      'PUT'
    ).then(data => {
      operations.incidentReport.update(objectifyDateStringFields(data))
      operations.incidentReport.loading(false)
    })
  }

  const reportTableColDef: GridColDef[] = [
    {
      field: 'id',
      headerName: '',
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant={'outlined'}
          startIcon={<Search />}
          onClick={() => onView(params.value)}
        >
          View
        </Button>
      )
    },
    {
      field: 'tracked',
      headerName: 'Tracked',
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Checkbox
          checked={params.value === true}
          onClick={() => toggleTrack(params.row.id, params.value)}
        />
      )
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 150,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      )
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      )
    },
    {
      field: 'managedBy',
      headerName: 'Reporter',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 150,
      valueGetter: (params: GridValueGetterParams) =>
        params.value !== null ? params.value.fullName : 'Unassigned',
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip variant={'filled'} color={'secondary'} label={params.value} />
      ),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Date Created',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 150,
      valueFormatter: (params: GridValueFormatterParams<Date>) =>
        params.value.toLocaleString().replaceAll('/', '-'),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      )
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: 150,
      valueFormatter: (params: GridValueFormatterParams<Date>) =>
        params.value.toLocaleString().replaceAll('/', '-'),
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant={'body2'}>
          <strong>{params.colDef.headerName}</strong>
        </Typography>
      )
    }
  ]

  return (
    <DataGrid
      columns={reportTableColDef}
      rows={incidentReports}
      pageSizeOptions={[15]}
      loading={loading}
      sx={{
        '.MuiDataGrid-cell:focus': {
          outline: 0
        },
        '.MuiDataGrid-cell:focus-within': {
          outline: 0
        },
        minHeight: 400,
        borderRadius: 4,
        color: props.theme.secondaryColor,
        border: 2,
        borderColor: '#d4d2d2'
      }}
      slots={{
        noRowsOverlay: () => (
          <Stack height="100%" alignItems="center" justifyContent="center">
            No reports found
          </Stack>
        )
      }}
      disableRowSelectionOnClick
      disableColumnSelector
      hideFooterSelectedRowCount
      disableColumnMenu
    />
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    incidentReport: state.incidentReport
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  incidentReport: incidentReportOperations
})

const IncidentReportTable = connect(
  mapStateToProps,
  mapDispatchToProps
)(IncidentReportTableFC) as React.FC<OwnProps>

export default IncidentReportTable
